import * as React from "react"
import { cn } from "../../lib/utils"

export interface AutoResizeTextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  minRows?: number
  maxRows?: number
}

const AutoResizeTextarea = React.forwardRef<HTMLTextAreaElement, AutoResizeTextareaProps>(
  ({ className, minRows = 1, maxRows = 10, value, onChange, ...props }, ref) => {
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null)
    const internalRef = React.useRef<HTMLTextAreaElement | null>(null)
    
    React.useImperativeHandle(ref, () => internalRef.current!, [])
    
    const combinedRef = React.useCallback(
      (instance: HTMLTextAreaElement | null) => {
        internalRef.current = instance
        textareaRef.current = instance
      },
      []
    )

    const adjustHeight = React.useCallback(() => {
      const textarea = textareaRef.current
      if (!textarea) return

      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto'
      
      // Calculate the line height
      const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight)
      const minHeight = lineHeight * minRows
      const maxHeight = lineHeight * maxRows
      
      // Set the height based on content, respecting min and max
      const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight)
      textarea.style.height = `${newHeight}px`
      
      // Add or remove scrollbar based on content
      textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden'
    }, [minRows, maxRows])

    React.useEffect(() => {
      adjustHeight()
    }, [value, adjustHeight])

    React.useEffect(() => {
      const textarea = textareaRef.current
      if (!textarea) return

      const handleInput = () => adjustHeight()
      textarea.addEventListener('input', handleInput)
      
      // Initial adjustment
      adjustHeight()

      return () => {
        textarea.removeEventListener('input', handleInput)
      }
    }, [adjustHeight])

    return (
      <textarea
        className={cn(
          "flex w-full rounded-lg bg-transparent px-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 resize-none transition-all duration-150",
          className
        )}
        ref={combinedRef}
        value={value}
        onChange={onChange}
        {...props}
      />
    )
  }
)

AutoResizeTextarea.displayName = "AutoResizeTextarea"

export { AutoResizeTextarea }